// src/features/ee/pcb/playbook/PCBPlayBook.jsx
// PCB-specific PlayBook configuration component aligned to Form_EE_PCB styling

import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import {
  Box,
  Divider,
  MenuItem,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PCB_BOARD_DEFAULTS, PCB_FALLBACK_DEFAULTS, PCB_PANELS, bestPcbPanel, pcbPanelLayout } from "../pcbCosting";

export default function PCBPlayBook({ data = {}, onChange }) {
  const theme = useTheme();
  const brandColor = theme.custom?.brandColor || "#ff6034";
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    surface: "#242728",
    textPrimary: "#F1F2F3",
    textSecondary: "#798286",
    border: "rgba(241, 242, 243, 0.08)",
    white: "#FFFFFF",
  };

  const FieldLabel = ({ label, tooltip }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ color: perlColors.textPrimary }}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="top">
          <InfoOutlinedIcon fontSize="small" sx={{ color: brandColor }} />
        </Tooltip>
      )}
    </Box>
  );

  const boardTypeBestParams = PCB_BOARD_DEFAULTS;
  const bestDefaults = PCB_FALLBACK_DEFAULTS;
  const PANEL_SIZES = PCB_PANELS;
  const sliderRanges = {
    goldFingers: { min: 0, max: 400, step: 1 },
    blindHoles: { min: 0, max: 5000, step: 10 },
    buriedHoles: { min: 0, max: 5000, step: 10 },
    ohp: { min: 0, max: 30, step: 0.5 },
  };

  const bestParams = useMemo(() => {
    const key = data?.boardType || data?.board_type || "";
    return boardTypeBestParams[key] || bestDefaults;
  }, [data?.boardType, data?.board_type]);

  const bestPanel = useMemo(
    () => bestPcbPanel(data) || { panelSizeIn: "", boardsPerPanel: 0, utilizationPct: 0 },
    [data?.boardLengthMm, data?.boardWidthMm]
  );

  const selectedPanel = useMemo(
    () => pcbPanelLayout(data.panelSizeIn, data.boardLengthMm, data.boardWidthMm),
    [data.panelSizeIn, data.boardLengthMm, data.boardWidthMm],
  );
  useEffect(() => {
    if (!selectedPanel) return;
    for (const [field, value] of Object.entries({ boardsPerPanel: selectedPanel.boardsPerPanel, utilization: selectedPanel.utilizationPct, orientationDegrees: selectedPanel.orientationDegrees })) {
      if (data[field] !== value) onChange?.(field, value, { derived: true });
    }
  }, [selectedPanel, data.boardsPerPanel, data.utilization, data.orientationDegrees, onChange]);

  const setValueIfDifferent = (field, value, override = true) => {
    if (String(data[field] ?? "") !== String(value ?? "") || data.overrideFields?.[field] !== override) {
      onChange?.(field, value, { override });
    }
  };

  const getBestValue = (field) => {
    switch (field) {
      case "boardThicknessMm":
        return bestParams.thickness;
      case "panelSizeIn":
        return bestPanel.panelSizeIn;
      case "layerCount":
        return bestParams.layers;
      case "cladLaminateCount":
        return bestParams.clad;
      case "cuAlThicknessOz":
        return bestParams.copperOz;
      case "drilledHoleCount":
        return bestParams.drillHoles;
      default:
        return "";
    }
  };

  // Respect incoming values on initial load so we don't override user provided data
  // Default all toggles to "Best in Class" on load/board type change; users can switch to "User Input"
  const cardSx = {
    border: `1px solid ${perlColors.border}`,
    borderRadius: 2,
    p: 1.5,
    bgcolor: alpha(perlColors.white, 0.02),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 1,
  };

  const toggleGroupSx = {
    "& .MuiToggleButton-root": {
      textTransform: "none",
      color: perlColors.textSecondary,
      borderColor: perlColors.border,
      fontSize: "0.78rem",
      padding: "3px 8px",
      minWidth: 76,
      height: 28,
      "&.Mui-selected": {
        color: "#000",
        backgroundColor: brandColor,
        borderColor: brandColor,
      },
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        bgcolor: perlColors.surface,
        "& .MuiMenuItem-root": {
          color: perlColors.textPrimary,
          fontFamily: theme.typography?.fontFamily,
        },
        "& .Mui-selected": {
          bgcolor: alpha(perlColors.white, 0.12),
        },
      },
    },
  };

  const renderBestCard = (fieldConfig) => {
    const { field, label, helper, modeKey, options } = fieldConfig;
    const mode = data.overrideFields?.[field] === true ? "user" : "best";
    const value = mode === "best" ? String(getBestValue(field) ?? "") : data?.[field] ?? "";
    const onModeChange = (newMode) => {
      if (!newMode) return;
      setValueIfDifferent(field, newMode === "best" ? getBestValue(field) : data[field] ?? getBestValue(field), newMode === "user");
    };

    const inputProps = {
      fullWidth: true,
      value,
      onChange: (e) => setValueIfDifferent(field, e.target.value),
      disabled: mode === "best",
      InputLabelProps: { shrink: true },
      InputProps: {
        sx: {
          color: perlColors.textPrimary,
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: perlColors.textPrimary,
            opacity: 1,
          },
        },
      },
      FormHelperTextProps: { sx: { color: perlColors.textSecondary } },
      helperText:
        mode === "best"
          ? `Auto-selected (${getBestValue(field) ?? "N/A"}) for Best in Class.`
          : helper,
    };

    return (
      <Grid key={field} size={{ xs: 12, md: 4 }}>
        <Box sx={cardSx}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <FieldLabel label={label} tooltip={helper} />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_, newMode) => onModeChange(newMode)}
                sx={{ ...toggleGroupSx }}
              >
                <ToggleButton value="best">Best in Class</ToggleButton>
                <ToggleButton value="user">User Input</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          {options ? (
            <TextField
              select
              {...inputProps}
              SelectProps={{ displayEmpty: true, MenuProps: selectMenuProps }}
            >
              <MenuItem value="">
                <em>Select panel preset</em>
              </MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField type="number" {...inputProps} />
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: perlColors.textPrimary }}>
        Fabrication PlayBook
      </Typography>

      <Divider sx={{ mb: 1 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Technical Controls
      </Typography>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            label="Material Tg*"
            value={data.materialTg || "155"}
            onChange={(e) => onChange?.("materialTg", e.target.value)}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            {["155", "175", "200"].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            label="Board Finish*"
            value={data.boardFinish || "ENIG"}
            onChange={(e) => onChange?.("boardFinish", e.target.value)}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            {["ENEPIG", "ENIG", "HASL", "HASLF", "Immersion Silver", "Immersion Tin"].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            label="Material Type*"
            value={data.materialType || "Mid loss"}
            onChange={(e) => onChange?.("materialType", e.target.value)}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            {["High loss", "High performance", "Low loss", "Mid loss", "Standard loss", "Ultra-low loss"].map(
              (opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              )
            )}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              alignItems: "center",
              border: `1px solid ${perlColors.border}`,
              borderRadius: 2,
              p: 1,
              bgcolor: alpha(perlColors.white, 0.02),
            }}
          >
            <FieldLabel label="Edge Plating" tooltip="Enable edge plating for edge connectors." />
            <Switch
              checked={Boolean(data.edgePlating)}
              onChange={(e) => onChange?.("edgePlating", e.target.checked)}
              color="primary"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              alignItems: "center",
              border: `1px solid ${perlColors.border}`,
              borderRadius: 2,
              p: 1,
              bgcolor: alpha(perlColors.white, 0.02),
            }}
          >
            <FieldLabel label="Unbalanced Stack" tooltip="Flag if the PCB uses an unbalanced stack-up." />
            <Switch
              checked={Boolean(data.unbalancedStack)}
              onChange={(e) => onChange?.("unbalancedStack", e.target.checked)}
              color="primary"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              alignItems: "center",
              border: `1px solid ${perlColors.border}`,
              borderRadius: 2,
              p: 1,
              bgcolor: alpha(perlColors.white, 0.02),
            }}
          >
            <FieldLabel label="Impedance" tooltip="Turn on impedance control requirements." />
            <Switch
              checked={Boolean(data.impedanceControl)}
              onChange={(e) => onChange?.("impedanceControl", e.target.checked)}
              color="primary"
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Best in Class
      </Typography>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {renderBestCard({
          field: "boardThicknessMm",
          label: "Board Thickness (mm)*",
          helper: "Set the finished thickness of the PCB.",
        })}
        {renderBestCard({
          field: "panelSizeIn",
          label: "Panel Size (in)*",
          helper: "Choose the fabrication panel preset for nesting.",
          options: PANEL_SIZES,
        })}
        {renderBestCard({
          field: "layerCount",
          label: "No. of Layers*",
          helper: "Copper layers in the stack.",
          modeKey: "layers",
        })}
        {renderBestCard({
          field: "cladLaminateCount",
          label: "No. of Clad Laminates*",
          helper: "Dielectric cores/plies.",
          modeKey: "clad",
        })}
        {renderBestCard({
          field: "cuAlThicknessOz",
          label: "Cu/Al Thickness (oz)*",
          helper: "Outer-layer plating thickness.",
          modeKey: "copper",
        })}
        {renderBestCard({
          field: "drilledHoleCount",
          label: "No. of Drilled Holes*",
          helper: "Total mechanical drills.",
          modeKey: "drills",
        })}
      </Grid>

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Connector & Via Counts
      </Typography>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {[
          {
            field: "goldFingerCount",
            label: "No. of Gold Fingers*",
            helper: "Total plated edge contacts required on the PCB.",
            range: sliderRanges.goldFingers,
          },
          {
            field: "blindHoleCount",
            label: "No. of Blind Holes*",
            helper: "Total blind vias to be drilled.",
            range: sliderRanges.blindHoles,
          },
          {
            field: "buriedHoleCount",
            label: "No. of Buried Holes*",
            helper: "Total buried vias to be drilled.",
            range: sliderRanges.buriedHoles,
          },
        ].map((sliderField) => {
          const sliderValue = Number(data?.[sliderField.field]) || 0;
          return (
            <Grid key={sliderField.field} size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  ...cardSx,
                  height: 170,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <FieldLabel label={sliderField.label} tooltip={sliderField.helper} />
                  <Typography variant="caption" sx={{ color: perlColors.textSecondary }}>
                    Range: {sliderField.range.min} - {sliderField.range.max}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Slider
                    value={sliderValue}
                    onChange={(_, v) => {
                      const numericValue = Array.isArray(v) ? v[0] : v;
                      setValueIfDifferent(sliderField.field, numericValue);
                    }}
                    min={sliderField.range.min}
                    max={sliderField.range.max}
                    step={sliderField.range.step}
                    valueLabelDisplay="on"
                    track={false}
                    sx={{
                      color: brandColor,
                      width: "100%",
                      "& .MuiSlider-thumb": { width: 22, height: 22 },
                      "& .MuiSlider-valueLabel": {
                        backgroundColor: "transparent",
                        color: perlColors.white,
                        fontWeight: 700,
                        transform: "translate(18px, -70%)",
                        "&:before": { display: "none" },
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: perlColors.textSecondary }}>
                  {sliderField.helper}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Overheads and Profit (OHP)
      </Typography>

      <Typography variant="body2" sx={{ color: perlColors.textSecondary }}>
        {data.overhead
          ? `$${Number(data.overhead.amount).toFixed(4)} per unit (${Number(data.overhead.effectivePercentage).toFixed(2)}% of base cost), calculated from benchmark components.`
          : "Calculated from benchmark components when you run Quick Cost."}
      </Typography>
      {(!selectedPanel || selectedPanel.boardsPerPanel < 1) && (
        <Typography color="error">The board does not fit the selected panel. Choose Best in Class or a larger panel.</Typography>
      )}

    </Box>
  );
}
